import React, { useState } from 'react'
import { CloseButton } from 'react-bootstrap'
import { FRONTEND_ROOT } from '../../constants'
import CustomModal from '../CustomModal/CustomModal'
import styles from './CreateWithAIModal.module.css'

// Inline icon helper so the modal stays self-contained (no asset dependencies).
const Icon = ({ d }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
)

// Mirrors the options on the standalone /create page so the modal feels native.
const OPTIONS = [
  {
    id: 'personal',
    title: 'Personal Website',
    icon: (
      <Icon d="M20.25 14.15v4.073a2.25 2.25 0 0 1-2.043 2.241l-.825.075a59.91 59.91 0 0 1-10.764 0l-.825-.075a2.25 2.25 0 0 1-2.043-2.241V14.15M18 8.25V6a2.25 2.25 0 0 0-2.25-2.25h-7.5A2.25 2.25 0 0 0 6 6v2.25m13.5 4.5c-3.262.998-6.706 1.5-10.5 1.5h-.5c-3.494 0-6.94-.502-10.214-1.5l-.286-.087a2.25 2.25 0 0 1-1.5-2.12V10.5A2.25 2.25 0 0 1 3.75 8.25h16.5A2.25 2.25 0 0 1 22.5 10.5v.643a2.25 2.25 0 0 1-1.5 2.12Z" />
    )
  },
  {
    id: 'portfolio',
    title: 'Portfolio',
    icon: (
      <Icon d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm1.294 6.336a6.721 6.721 0 0 1-3.17.789 6.721 6.721 0 0 1-3.168-.789 3.376 3.376 0 0 1 6.338 0Z" />
    )
  },
  {
    id: 'business',
    title: 'Business Website',
    icon: (
      <Icon d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
    )
  },
  {
    id: 'sales-proposal',
    title: 'Sales Proposal',
    icon: (
      <Icon d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    )
  },
  {
    id: 'internal-tool',
    title: 'Internal Tool',
    icon: <Icon d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
  }
]

const buildCreateUrl = (workflowId) => {
  const url = new URL('/create/', FRONTEND_ROOT)
  url.searchParams.set('workflow', workflowId)
  return url.toString()
}

const CreateWithAIModal = ({ show, onHide }) => {
  const [prompt, setPrompt] = useState('')

  const handlePickWorkflow = (workflowId) => {
    window.open(buildCreateUrl(workflowId), '_blank')
  }

  const handleSend = () => {
    const trimmed = prompt.trim()
    if (!trimmed) return
    localStorage.setItem('wizardPrompt', trimmed)
    window.open(buildCreateUrl('custom'), '_blank')
  }

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      aria-labelledby="create-with-ai-modal"
      centered
      enforceFocus={false}
      size="md"
      className={styles.modal}
      mobileCloseButton={false}
    >
      <div className={styles.container}>
        <CloseButton className={styles.closeButton} onClick={onHide} aria-label="Close" variant="white" />

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.brand}>
            <img src="/assets/logo-white.svg" alt="tiiny.host" className={styles.brandLogo} />
            <span className={styles.brandDivider} aria-hidden="true">
              |
            </span>
            <span className={styles.brandProduct}>Create</span>
          </div>
          <h2 className={styles.title}>What do you want to build today?</h2>
        </div>

        {/* Options */}
        <div className={styles.options}>
          {OPTIONS.map((option) => (
            <button
              key={option.id}
              className={styles.optionButton}
              type="button"
              onClick={() => handlePickWorkflow(option.id)}
            >
              <span className={styles.optionIcon}>{option.icon}</span>
              <span className={styles.optionTitle}>{option.title}</span>
            </button>
          ))}
        </div>

        {/* Composer */}
        <p className={styles.composerLabel}>&hellip;or just describe what you want</p>
        <div className={styles.composer}>
          <textarea
            className={styles.composerInput}
            placeholder="What do you want to build today?"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            rows={3}
          />
          <button className={styles.composerSend} type="button" aria-label="Send" onClick={handleSend}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
    </CustomModal>
  )
}

export default CreateWithAIModal
